import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

import { AutoCompleteSelectUI, FormLabelUI, PhoneBase, TabsUI } from '../../Interface';
import addNotification from '../../../utils/addNotification';
import { NotificationType } from '../../../config';
import { useLoader } from '../../../provider/LoaderProvider';
import { WmsType } from '../../../config/wms/wmsType';
import wmsBaseService from '../../../service/wms/wmsBase.service';
import itemService from '../../../service/item.service';
import ItemsDialog from '../../ProductionMaterial/ItemsDialog';
import ItemOutgoing from '../Components/ItemOutgoing';
import masterDataService from '../../../service/masterData.service';
import { generateAutocompleteItems } from '../../../utils/helper';
import { calculateWmsStockValidation } from '../../../utils/wmsLineStockValidation';
import WmsTabContent from './WmsTabContent';
import TargetDocTab from './TargetDocTab';

const wmsType = WmsType.InventoryTransfer;

const InventoryTransferDoc = () => {
  const navigate = useNavigate();
  const { wmsEntry } = useParams();
  const { showLoader, hideLoader } = useLoader();
  const itemInputRef = useRef(null);

  const [activeTab, setActiveTab] = useState(0);
  const [head, setHead] = useState(null);
  const [lines, setLines] = useState([]);
  const [itemFilter, setItemFilter] = useState('');
  const [items, setItems] = useState([]);
  const [identifiedItem, setIdentifiedItem] = useState(null);
  const [identifiedQty, setIdentifiedQty] = useState(1);
  const [warehouseData, setWarehouseData] = useState([]);
  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const [sourceBinId, setSourceBinId] = useState(null);
  const [targetWhsCode, setTargetWhsCode] = useState('');
  const [targetBinId, setTargetBinId] = useState(null);
  const [note, setNote] = useState('');

  const resolvedHeadId = useMemo(() => Number(wmsEntry), [wmsEntry]);

  const fetchHeadAndLines = async () => {
    if (!resolvedHeadId) return;

    showLoader();
    try {
      const [headRes, linesRes] = await Promise.all([
        wmsBaseService.getRow(resolvedHeadId, { wmsType }),
        wmsBaseService.getLinesByHeadId(resolvedHeadId, { wmsType }),
      ]);

      const nextHead = headRes?.row ?? headRes?.data ?? headRes ?? null;
      const nextLines = Array.isArray(linesRes?.rows) ? linesRes.rows : Array.isArray(linesRes) ? linesRes : [];

      setHead(nextHead);
      setLines(nextLines);
    } catch (e) {
      addNotification({
        content: 'A dokumentum adatai nem tölthetők be.',
        type: NotificationType.ERROR,
      });
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    fetchHeadAndLines();
  }, [resolvedHeadId]);

  useEffect(() => {
    masterDataService.warehouseToForm().then((data) => {
      const rows = data ?? [];
      setWarehouseData(rows);
      setWarehouseOptions(generateAutocompleteItems(rows, 'name', 'code'));
    });
  }, []);

  useEffect(() => {
    setTargetWhsCode(head?.toWhsCode ?? '');
    setTargetBinId(head?.toBinId ?? null);
    setNote(head?.note ?? '');
  }, [head]);

  const sourceWarehouse = useMemo(
    () => warehouseData.find((warehouse) => warehouse.code === head?.whsCode) ?? null,
    [warehouseData, head?.whsCode]
  );
  const isSourceBinManaged = Boolean(sourceWarehouse?.binManaged);
  const sourceBins = sourceWarehouse?.bin ?? [];
  const sourceBinOptions = useMemo(() => generateAutocompleteItems(sourceBins, 'binCode', 'binId'), [sourceBins]);
  const selectedSourceBin = sourceBins.find((bin) => bin.binId === sourceBinId) ?? null;

  useEffect(() => {
    if (!isSourceBinManaged) {
      setSourceBinId(null);
      return;
    }

    const hasSelectedBin = sourceBins.some((bin) => bin.binId === sourceBinId);
    if (!hasSelectedBin) {
      setSourceBinId(head?.binId ?? null);
    }
  }, [isSourceBinManaged, sourceBins, sourceBinId, head?.binId]);

  const handleAddLine = async (selectedItem, qtyOverride = null) => {
    if (!selectedItem?.itemCode) return;

    const resolvedQty = Number(qtyOverride ?? selectedItem?.quantity ?? selectedItem?.qty ?? 1) || 1;
    const quantityUnitId =
      selectedItem?.quantityUnitId ?? selectedItem?.item?.quantityUnit?.id ?? selectedItem?.quantityUnit?.id ?? null;
    const quantityUnitCode =
      selectedItem?.quantityUnitCode ??
      selectedItem?.item?.quantityUnit?.code ??
      selectedItem?.quantityUnit?.code ??
      null;

    showLoader();
    try {
      await wmsBaseService.createLine({
        id: undefined,
        wmsType,
        wmsHeadId: resolvedHeadId,
        itemCode: selectedItem.itemCode,
        quantity: resolvedQty,
        //lineStatus: 'C',
        whsCode: head?.whsCode ?? selectedItem?.whsCode ?? null,
        binId: selectedItem?.binId ?? sourceBinId ?? null,
        batchSn: selectedItem?.batchSn ?? null,
        batchSnId: selectedItem?.batchSnId ?? selectedItem?.batchId ?? selectedItem?.snBinId ?? null,
        binCode: selectedItem?.binCode ?? selectedSourceBin?.binCode ?? null,
        quantityUnitId,
        quantityUnitCode,
      });

      setItemFilter('');
      setItems([]);
      setIdentifiedItem(null);
      setIdentifiedQty(1);
      await fetchHeadAndLines();
      setTimeout(() => {
        if (itemInputRef.current) itemInputRef.current.focus();
      }, 0);
    } catch (e) {
      addNotification({
        content: 'A tétel rögzítése sikertelen.',
        type: NotificationType.ERROR,
      });
    } finally {
      hideLoader();
    }
  };

  const handleSearchItem = async () => {
    const query = itemFilter.trim();
    if (!query) return;

    if (isSourceBinManaged) {
      const normalizedQuery = query.toLowerCase();
      const matchedBin = sourceBins.find(
        (bin) =>
          String(bin?.binCode ?? '')
            .trim()
            .toLowerCase() === normalizedQuery
      );

      if (matchedBin) {
        setSourceBinId(matchedBin.binId);
        setItemFilter('');
        addNotification({
          content: `Kiadó rakhely kiválasztva: ${matchedBin.binCode}`,
          type: NotificationType.SUCCESS,
        });
        return;
      }

      if (!sourceBinId) {
        addNotification({
          content: 'A kiadó raktár rakhelyes, előbb válassz kiadó rakhelyet.',
          type: NotificationType.WARNING,
        });
        return;
      }
    }

    showLoader();
    try {
      const data = await itemService.findItemFromStock({
        query,
        fullSearch: false,
        whsCode: head?.whsCode,
        binId: sourceBinId ?? undefined,
      });

      if (!Array.isArray(data) || data.length === 0) {
        addNotification({
          content: `Nincs találat a megadott cikkre. (${query})`,
          type: NotificationType.WARNING,
        });
        return;
      }

      if (data.length === 1) {
        setIdentifiedItem(data[0]);
        setIdentifiedQty(1);
        setItemFilter('');
        return;
      }

      setItems(data);
      setItemFilter('');
    } catch (e) {
      addNotification({
        content: 'A cikk keresése sikertelen.',
        type: NotificationType.ERROR,
      });
    } finally {
      hideLoader();
    }
  };

  const handleDeleteLine = async (line) => {
    const lineId = line?.id;
    if (!lineId) return;

    showLoader();
    try {
      await wmsBaseService.deleteLine(lineId, wmsType);
      setLines((prev) => prev.filter((x) => x.id !== lineId));
    } catch (e) {
      addNotification({
        content: 'A sor törlése sikertelen.',
        type: NotificationType.ERROR,
      });
    } finally {
      hideLoader();
    }
  };

  const handleConfirmIdentifiedItem = async () => {
    if (!identifiedItem?.itemCode) {
      addNotification({
        content: 'Nincs kiválasztott cikk.',
        type: NotificationType.WARNING,
      });
      return;
    }

    if (isSourceBinManaged && !sourceBinId) {
      addNotification({
        content: 'A kiadó raktár rakhelyes, ezért a kiadó rakhely megadása kötelező.',
        type: NotificationType.WARNING,
      });
      return;
    }

    if (Number(identifiedQty) <= 0) {
      addNotification({
        content: 'A mennyiségnek 0-nál nagyobbnak kell lennie.',
        type: NotificationType.WARNING,
      });
      return;
    }

    if (isOverStockLimit) {
      addNotification({
        content: 'A rögzített és rögzítendő mennyiség együtt nem haladhatja meg a készletet.',
        type: NotificationType.WARNING,
      });
      return;
    }

    await handleAddLine(identifiedItem, identifiedQty);
  };

  const identifiedQtyUnitCode =
    identifiedItem?.quantityUnitCode ??
    identifiedItem?.item?.quantityUnit?.code ??
    identifiedItem?.quantityUnit?.code ??
    '';

  // const resolvedIdentifiedQty = identifiedQty === '' ? 1 : Number(identifiedQty) || 1;

  const { identifiedStockQty, alreadyRecordedQty, nextTotalQty, isOverStockLimit } = useMemo(
    () =>
      calculateWmsStockValidation({
        identifiedItem,
        lines,
        identifiedQty,
      }),
    [identifiedItem, lines, identifiedQty]
  );

  const openQty = Math.max(identifiedStockQty - alreadyRecordedQty, 0);

  const handleSaveDoc = async () => {
    if (!head || !resolvedHeadId) return;

    if (!targetWhsCode) {
      addNotification({
        content: 'A cél raktár megadása kötelező.',
        type: NotificationType.WARNING,
      });
      return;
    }

    showLoader();
    try {
      const newDocNum = await wmsBaseService.generateTargetDoc(resolvedHeadId, {
        wmsType,
        targetWhsCode: targetWhsCode || null,
        targetBinId: targetBinId ?? null,
        note,
      });

      // visszanavigálni az inventoryTransfer-re
      navigate('/app/wms/inventoryTransfer');

      addNotification({
        content: `Sikeres mentés!`,
        type: NotificationType.SUCCESS,
      });
    } catch (e) {
      addNotification({
        content: 'A bizonylat mentése sikertelen.',
        type: NotificationType.ERROR,
      });
    } finally {
      hideLoader();
    }
  };

  const tabs = [
    {
      id: 0,
      name: 'WMS',
      content: <WmsTabContent lines={lines} onDeleteLine={handleDeleteLine} />,
    },
    {
      id: 1,
      name: 'Lezárás',
      content: (
        <TargetDocTab
          targetWhsCode={targetWhsCode}
          targetBinId={targetBinId}
          note={note}
          warehouseData={warehouseData}
          warehouseOptions={warehouseOptions}
          onTargetWhsChange={(nextWhsCode, whs) => {
            setTargetWhsCode(nextWhsCode);
            if (whs?.binManaged) {
              setTargetBinId(whs.dispatchBin ?? null);
            } else {
              setTargetBinId(null);
            }
          }}
          onTargetBinChange={setTargetBinId}
          onNoteChange={setNote}
          onSave={handleSaveDoc}
        />
      ),
    },
  ];

  return (
    <PhoneBase name="InventoryTransferDoc">
      <Typography variant="h5" sx={{ fontWeight: 400, py: 1, px: 2 }}>
        Készletáttárolás
      </Typography>
      <Box sx={{ px: 2, pb: 1 }}>
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            p: 1.5,
            bgcolor: 'grey.100',
          }}
        >
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={3} md>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Raktár: {head?.whsCode || '-'}
              </Typography>
            </Grid>

            {isSourceBinManaged ? (
              <Grid item xs={9} md="auto" sx={{ width: { xs: '100%', md: 'auto' } }}>
                <Box sx={{ minWidth: 200, width: '100%', maxWidth: 360, bgcolor: 'white' }}>
                  <AutoCompleteSelectUI
                    selectedValue={sourceBinId}
                    selectedLabelValue={selectedSourceBin?.binCode ?? ''}
                    onChange={(_e, newVal) => {
                      setSourceBinId(newVal?.value ?? null);
                    }}
                    dataset={sourceBinOptions}
                    placeholder="Rakhely"
                    size="small"
                    variant="outlined"
                  />
                </Box>
              </Grid>
            ) : null}
          </Grid>
        </Box>

        <ItemOutgoing
          itemFilter={itemFilter}
          onItemFilterChange={setItemFilter}
          onSearch={handleSearchItem}
          itemInputRef={itemInputRef}
          identifiedItem={identifiedItem}
          identifiedQty={identifiedQty}
          onIdentifiedQtyChange={(rawValue) => {
            if (rawValue === '') {
              setIdentifiedQty('');
              return;
            }

            const parsedQty = Number(rawValue);
            if (Number.isNaN(parsedQty)) return;

            setIdentifiedQty(Math.max(1, parsedQty));
          }}
          onIdentifiedQtyBlur={() => {
            if (identifiedQty === '' || Number(identifiedQty) <= 0) {
              setIdentifiedQty(1);
            }
          }}
          identifiedQtyUnitCode={identifiedQtyUnitCode}
          onConfirmItem={handleConfirmIdentifiedItem}
          onClearIdentified={() => {
            setIdentifiedItem(null);
            setIdentifiedQty(1);
          }}
          identifiedStockQty={identifiedStockQty}
          alreadyRecordedQty={alreadyRecordedQty}
          openQty={openQty}
          isOverStockLimit={isOverStockLimit}
        />
      </Box>

      <TabsUI activeTab={activeTab} handleTabClick={setActiveTab} disablePadding>
        {tabs.map((tab) => (
          <div key={tab.id} name={tab.name}>
            {tab.content}
          </div>
        ))}
      </TabsUI>

      <ItemsDialog
        open={items.length > 1}
        handleClose={() => setItems([])}
        items={items}
        addItem={(item) => {
          setIdentifiedItem(item);
          setIdentifiedQty(1);
        }}
      />
    </PhoneBase>
  );
};

export default InventoryTransferDoc;
